import { useEffect, useState, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { useModalAberto } from '../contexts/ModalContext';
import EnderecoFields from '../components/EnderecoFields';
import { ENDERECO_VAZIO } from '../utils/cep';

// ─── Aba Oficina ─────────────────────────────────────────────────────────────
function AbaOficina() {
  const [form, setForm] = useState({ nomeOficina: '', cnpj: '', telefone: '', ...ENDERECO_VAZIO });
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    api.get('/configuracao').then(r => {
      const d = r.data;
      setForm({
        nomeOficina: d.nomeOficina || '', cnpj: d.cnpj || '', telefone: d.telefone || '',
        cep: d.cep || '', endereco: d.endereco || '', numero: d.numero || '', bairro: d.bairro || '', cidade: d.cidade || '', estado: d.estado || '',
      });
      if (d.logoBase64) setPreview(d.logoBase64);
    });
  }, []);

  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const salvar = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (logoFile) fd.append('logo', logoFile);
      await api.put('/configuracao', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Configurações salvas!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={salvar} className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-semibold text-gray-700 mb-4">Logo da Oficina</h2>
        <div className="flex items-center gap-4">
          {preview
            ? <img src={preview} alt="Logo" className="w-24 h-24 object-contain border border-gray-200 rounded-lg" />
            : <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-sm text-center">Sem logo</div>}
          <div>
            <button type="button" onClick={() => fileRef.current.click()} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">{preview ? 'Trocar Logo' : 'Enviar Logo'}</button>
            <p className="text-xs text-gray-400 mt-1">PNG ou JPG, máx 2MB</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
        <h2 className="font-semibold text-gray-700">Dados da Oficina</h2>
        {[
          { key: 'nomeOficina', label: 'Nome da Oficina *', required: true },
          { key: 'cnpj', label: 'CNPJ' },
          { key: 'telefone', label: 'Telefone' },
        ].map(field => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
            <input required={field.required} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form[field.key]} onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))} />
          </div>
        ))}
        <EnderecoFields form={form} setForm={setForm} />
      </div>
      <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-blue-700 disabled:opacity-50">
        {loading ? 'Salvando...' : 'Salvar Configurações'}
      </button>
    </form>
  );
}

// ─── Aba Tema ─────────────────────────────────────────────────────────────────
function AbaTema() {
  const { tema, setTema } = useTheme();

  const OPCOES = [
    { valor: 'claro', titulo: 'Claro', icone: '☀️', desc: 'Fundo branco, ideal para ambientes iluminados' },
    { valor: 'escuro', titulo: 'Escuro', icone: '🌙', desc: 'Fundo escuro, mais confortável à noite' },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm p-5">
      <h2 className="font-semibold text-gray-700 mb-1">Aparência</h2>
      <p className="text-sm text-gray-500 mb-4">Escolha o tema de exibição do aplicativo.</p>
      <div className="grid grid-cols-2 gap-3">
        {OPCOES.map(op => (
          <button
            key={op.valor}
            type="button"
            onClick={() => setTema(op.valor)}
            className={`text-left rounded-xl border-2 p-4 transition-colors ${
              tema === op.valor
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-2xl">{op.icone}</span>
              {tema === op.valor && <span className="text-blue-600 text-lg font-bold">✓</span>}
            </div>
            <p className="font-semibold text-gray-800">{op.titulo}</p>
            <p className="text-xs text-gray-500 mt-0.5">{op.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Aba Usuários ─────────────────────────────────────────────────────────────
const PERFIL_LABEL = { admin: 'Administrativo', mecanico: 'Mecânico', proprietario: 'Proprietário/mecânico' };

const FORM_VAZIO = { nome: '', email: '', telefone: '', senha: '', perfil: 'mecanico', ...ENDERECO_VAZIO };

function AbaUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [modal, setModal] = useState(null); // null | { modo: 'criar' | 'editar' | 'senha', usuario? }
  useModalAberto(!!modal);
  const [form, setForm] = useState(FORM_VAZIO);
  const [novaSenha, setNovaSenha] = useState('');
  const [verNovaSenha, setVerNovaSenha] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const carregar = () => api.get('/auth/usuarios').then(r => setUsuarios(r.data));
  useEffect(() => { carregar(); }, []);

  const abrirCriar = () => { setForm(FORM_VAZIO); setModal({ modo: 'criar' }); };
  const abrirEditar = u => {
    setForm({
      nome: u.nome, email: u.email || '', telefone: u.telefone || '', senha: '', perfil: u.perfil,
      cep: u.cep || '', endereco: u.endereco || '', numero: u.numero || '', bairro: u.bairro || '', cidade: u.cidade || '', estado: u.estado || '',
    });
    setModal({ modo: 'editar', usuario: u });
  };
  const abrirSenha = u => { setNovaSenha(''); setVerNovaSenha(false); setModal({ modo: 'senha', usuario: u }); };

  const resetarSenha = async e => {
    e.preventDefault();
    if (!novaSenha) return;
    setSalvando(true);
    try {
      await api.put(`/auth/usuarios/${modal.usuario.id}`, { senha: novaSenha });
      toast.success(`Senha de ${modal.usuario.nome} redefinida!`);
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao redefinir senha'); }
    finally { setSalvando(false); }
  };

  const salvar = async e => {
    e.preventDefault();
    setSalvando(true);
    try {
      const CAMPOS_ENDERECO = ['cep', 'endereco', 'numero', 'bairro', 'cidade', 'estado'];
      if (modal.modo === 'criar') {
        // Para mecânicos, usar o telefone como login (email field) se e-mail não fornecido
        const payload = { ...form };
        if (form.perfil === 'mecanico' && !form.email) payload.email = form.telefone;
        await api.post('/auth/usuarios', payload);
        toast.success('Usuário criado!');
      } else {
        const payload = { nome: form.nome, perfil: form.perfil, telefone: form.telefone };
        CAMPOS_ENDERECO.forEach(k => { payload[k] = form[k]; });
        if (form.email) payload.email = form.email;
        else if (form.perfil === 'mecanico') payload.email = form.telefone;
        if (form.senha) payload.senha = form.senha;
        await api.put(`/auth/usuarios/${modal.usuario.id}`, payload);
        toast.success('Usuário atualizado!');
      }
      setModal(null);
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro ao salvar'); }
    finally { setSalvando(false); }
  };

  const deletar = async u => {
    if (!window.confirm(`Remover "${u.nome}"?`)) return;
    try {
      await api.delete(`/auth/usuarios/${u.id}`);
      toast.success('Usuário removido');
      carregar();
    } catch (err) { toast.error(err.response?.data?.erro || 'Erro'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-gray-700">Usuários do Sistema</h2>
        <button onClick={abrirCriar} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">+ Novo Usuário</button>
      </div>

      <div className="space-y-2">
        {usuarios.map(u => (
          <div key={u.id} className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-gray-800">{u.nome}</p>
              <p className="text-sm text-gray-500">
                {u.perfil === 'mecanico' ? (u.telefone || u.email) : u.email}
              </p>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${u.perfil === 'admin' ? 'bg-blue-100 text-blue-800' : u.perfil === 'proprietario' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                {PERFIL_LABEL[u.perfil] || u.perfil}
              </span>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <button onClick={() => abrirEditar(u)} className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">✏️ Editar</button>
              <button onClick={() => abrirSenha(u)} className="text-sm px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 text-amber-700">🔑 Senha</button>
              {u.email !== 'admin@oficina.com' && (
                <button onClick={() => deletar(u)} className="text-sm px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 text-red-600">🗑️</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal criar/editar */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-gray-800 text-lg mb-4">{modal.modo === 'criar' ? 'Novo Usuário' : 'Editar Usuário'}</h3>
            <form onSubmit={salvar} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil *</label>
                <select required className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.perfil} onChange={e => setForm(f => ({ ...f, perfil: e.target.value }))}>
                  <option value="mecanico">Mecânico</option>
                  <option value="admin">Administrativo</option>
                  <option value="proprietario">Proprietário/mecânico</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input required className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                <input required={form.perfil === 'mecanico'} type="tel" placeholder="(00) 00000-0000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} />
              </div>
              {form.perfil !== 'mecanico' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
                  <input required={form.perfil !== 'mecanico'} type="email"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
              )}
              <EnderecoFields form={form} setForm={setForm} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{modal.modo === 'criar' ? 'Senha *' : 'Nova Senha (deixe em branco para não alterar)'}</label>
                <div className="relative">
                  <input required={modal.modo === 'criar'} type={form._verSenha ? 'text' : 'password'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-10 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={form.senha} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} />
                  <button type="button" tabIndex={-1}
                    onClick={() => setForm(f => ({ ...f, _verSenha: !f._verSenha }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg px-1">
                    {form._verSenha ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModal(null)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={salvando} className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50">
                  {salvando ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal resetar senha */}
      {modal?.modo === 'senha' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-gray-800 text-lg mb-1">Redefinir Senha</h3>
            <p className="text-sm text-gray-500 mb-4">Usuário: <span className="font-semibold text-gray-700">{modal.usuario.nome}</span></p>
            <form onSubmit={resetarSenha} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova Senha *</label>
                <div className="relative">
                  <input required type={verNovaSenha ? 'text' : 'password'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-10 text-base focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Digite a nova senha..."
                    value={novaSenha} onChange={e => setNovaSenha(e.target.value)} />
                  <button type="button" tabIndex={-1}
                    onClick={() => setVerNovaSenha(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-lg px-1">
                    {verNovaSenha ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModal(null)} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={salvando} className="flex-1 bg-amber-500 text-white py-2.5 rounded-xl font-medium hover:bg-amber-600 disabled:opacity-50">
                  {salvando ? 'Salvando...' : '🔑 Redefinir'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────
export default function Configuracoes() {
  const [aba, setAba] = useState('oficina');

  return (
    <div className="max-w-xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-5">Configurações</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        {[['oficina', '🏪 Oficina'], ['usuarios', '👤 Usuários'], ['tema', '🎨 Tema']].map(([val, lbl]) => (
          <button key={val} onClick={() => setAba(val)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${aba === val ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {aba === 'oficina' && <AbaOficina />}
      {aba === 'usuarios' && <AbaUsuarios />}
      {aba === 'tema' && <AbaTema />}
    </div>
  );
}
